import React, { useContext, useEffect } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import PropTypes from 'prop-types';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { List } from '@one-thd/sui-atomic-components';
import {
  params, string as stringType, fragment, customType, useDataModel, arrayOf, bool
} from '@thd-nucleus/data-sources';
import { NavigationItem } from './NavigationItem';

const SideNavigation = ({
  componentId,
  componentClass = '',
  componentPosition = 1,
  livePreviewData = null
}) => {
  const isPreview = !!livePreviewData;

  useEffect(() => {
    if (window.LIFE_CYCLE_EVENT_BUS?.lifeCycle?.trigger) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('side-navigation.ready');
    }
  }, []);

  const { data, loading, error } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass
    },
    skip: isPreview
  });

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  if (!isPreview && (!data || error || loading)) return null;

  const content = isPreview ? livePreviewData : data?.component;
  // Live preview only rebuilds navigationList when the collection is non-empty, so read the collection first
  // Entries newly linked in Contentful only carry sys.id until the experience reformats them
  const navs = isPreview
    ? (content?.navigationListCollection?.items ?? content?.navigationList)
      ?.map((nav) => ({ ...nav, id: nav?.id || nav?.sys?.id }))
      .filter((nav) => nav.id)
    : content?.navigationList;

  if (isMobile) return null;
  return (
    <ImpressionProvider data={{
      id: componentId,
      component: SideNavigation.displayName,
      name: SideNavigation.displayName,
      type: 'content',
      position: ''
    }}
    >
      <div
        data-component-id={componentId}
        id={`${componentClass}-${componentPosition}-${componentId}`}
        data-component-name={SideNavigation.displayName}
        data-component-position={componentPosition}
        data-testid="items-list"
        data-component={SideNavigation.displayName}
        className="side-navigation sui-bg-primary sui-font-sans sui-hidden lg:sui-inline"
        {...(isPreview && {
          'data-contentful-entry-id': componentId,
          'data-contentful-field-id': 'navigationList'
        })}
      >
        <List type="plain">
          {navs?.map((nav, index) => (
            <NavigationItem
              // The same entry can be linked more than once
              key={`${nav.id}-${index}`}
              componentId={nav.id}
              componentPosition={index + 1}
              link={nav.link}
              title={nav.title}
              heading={nav.heading}
              isPreview={isPreview}
            />
          ))}
        </List>
      </div>
    </ImpressionProvider>
  );
};

const navItemShape = PropTypes.shape({
  id: PropTypes.string,
  title: PropTypes.string,
  link: PropTypes.string,
  heading: PropTypes.bool
});

SideNavigation.propTypes = {
  componentId: PropTypes.string.isRequired,
  componentClass: PropTypes.string,
  componentPosition: PropTypes.number,
  // landing-page's getComponentData passes false when the entry has no live data
  livePreviewData: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    navigationList: PropTypes.arrayOf(navItemShape),
    navigationListCollection: PropTypes.shape({
      items: PropTypes.arrayOf(navItemShape)
    })
  })])
};

SideNavigation.displayName = 'SideNavigation';

SideNavigation.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass')
      .enum(['SideNavigation'])
      .isRequired()
  }).shape({
    SideNavigation: fragment().shape({
      id: stringType(),
      componentName: stringType(),
      navigationList: arrayOf({
        id: stringType(),
        title: stringType(),
        link: stringType(),
        heading: bool()
      })
    })
  })
};

export { SideNavigation };
